import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import cookie from 'react-cookie';
import * as Sentry from '@sentry/react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  createEmptySnapshot,
  hasResumableProgress,
  normalizeSnapshot,
} from './snapshot';
import {
  consumePostAJobFlowCompleted,
  createLocalStorageFlowStorage,
} from './storage';
import {
  mergeAnswerPatch,
  mergeParams,
  readFlowQueryParams,
  buildFlowSearch,
} from './queryParams';
import {
  trackPostFlowRestarted,
  trackPostFlowResumed,
  trackPostFlowStepViewed,
} from './analytics';
import {
  STEP_IDS,
  getNextStepId,
  getPathForStepId,
  getProgressFraction,
  getSkipStepId,
  getStepIdFromPath,
  resolveBackFromVisited,
  trimVisitedToStep,
} from './steps';

const PostAJobFlowContext = createContext(null);

const defaultStorage = createLocalStorageFlowStorage();

const readCookie = (name) => {
  try {
    const value = cookie.load(name);
    return value == null || value === '' ? null : value;
  } catch (error) {
    Sentry.captureException(new Error('Failed to read PostAJob flow cookie'));
    return null;
  }
};

export const PostAJobFlowProvider = ({
  children,
  storage = defaultStorage,
}) => {
  const history = useHistory();
  const location = useLocation();
  const storageRef = useRef(storage);
  const transitionBusyRef = useRef(false);
  const prepareExitRef = useRef(null);
  const resumeCheckedRef = useRef(false);
  const skipPathSyncRef = useRef(false);
  // Original landing query. Restart / Start again rebuild from this (Fillout
  // parity: keep what the user arrived with; never restore typed-only answers).
  const landingSearchRef = useRef('');

  // Start empty for SSR; hydrate from storage on the client.
  const [snapshot, setSnapshot] = useState(() => createEmptySnapshot());
  const [offerResume, setOfferResume] = useState(false);
  // False until hydrate finishes (and any mid-flow → intent redirect has landed).
  const [resumeDecisionReady, setResumeDecisionReady] = useState(false);
  const pendingIntentRedirectRef = useRef(false);

  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;

  const persist = useCallback((updater) => {
    setSnapshot((prev) => {
      const draft = typeof updater === 'function' ? updater(prev) : updater;
      if (draft === prev) {
        return prev;
      }
      const normalized = normalizeSnapshot(draft);
      storageRef.current.save(normalized);
      snapshotRef.current = normalized;
      return normalized;
    });
  }, []);

  const applyQueryToSnapshot = useCallback((base, search) => {
    const { answersPatch, params: queryParams } = readFlowQueryParams(
      search,
      readCookie
    );
    const prefilledKeys = new Set(
      Array.isArray(base.prefilledKeys) ? base.prefilledKeys : []
    );
    Object.keys(answersPatch || {}).forEach((key) => {
      const incoming = answersPatch[key];
      if (incoming == null || incoming === '') {
        return;
      }
      const current = base.answers ? base.answers[key] : null;
      const isEmpty =
        current == null ||
        current === '' ||
        (Array.isArray(current) && current.length === 0);
      if (isEmpty) {
        prefilledKeys.add(key);
      }
    });
    return {
      ...base,
      answers: mergeAnswerPatch(base.answers, answersPatch),
      params: mergeParams(base.params, queryParams),
      prefilledKeys: Array.from(prefilledKeys),
    };
  }, []);

  // Client-only: restore session, then gap-fill from URL/cookies; offer resume.
  // useLayoutEffect so mid-flow deep links redirect before the step paints.
  useLayoutEffect(() => {
    if (resumeCheckedRef.current) {
      return;
    }
    resumeCheckedRef.current = true;

    const loaded = storageRef.current.load();
    let fromStorage = loaded ? normalizeSnapshot(loaded) : null;

    // After a successful post, ignore any leftover draft and force intent.
    if (consumePostAJobFlowCompleted()) {
      storageRef.current.clear();
      fromStorage = null;
    }

    let next = fromStorage || createEmptySnapshot();

    const search = history.location.search || location.search || '';
    landingSearchRef.current = search;
    next = applyQueryToSnapshot(next, search);

    snapshotRef.current = next;
    setSnapshot(next);

    // Keep an existing draft in sync when URL fills empty answer slots / params.
    // Do not write a brand-new draft for URL-only visits (avoids sticky prefills).
    if (fromStorage) {
      storageRef.current.save(next);
    }

    const pathStep = getStepIdFromPath(
      history.location.pathname || location.pathname
    );
    const onFirstPage = !pathStep || pathStep === STEP_IDS.INTENT;

    // Mid-flow URL without a resumable draft (cold deep link, or Back after
    // post) → start clean on intent. Prefer stored progress over URL-only
    // prefills so ?role= on /post/about-you does not count as "in progress".
    if (
      pathStep &&
      pathStep !== STEP_IDS.INTENT &&
      !(fromStorage && hasResumableProgress(fromStorage))
    ) {
      storageRef.current.clear();
      next = applyQueryToSnapshot(createEmptySnapshot(), search);
      snapshotRef.current = next;
      setSnapshot(next);
      pendingIntentRedirectRef.current = true;
      history.replace(
        `${getPathForStepId(STEP_IDS.INTENT)}${buildFlowSearch(
          next.params,
          search
        )}`
      );
      // flowReady flips once location is intent (see effect below).
      return;
    }

    if (onFirstPage && fromStorage && hasResumableProgress(fromStorage)) {
      // Keep saved currentStepId until the user chooses Continue / Start again.
      skipPathSyncRef.current = true;
      setOfferResume(true);
    }
    setResumeDecisionReady(true);
    // Mount-only: hydrate once per provider instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Finish bootstrap only after a forced mid-flow → intent redirect has applied,
  // so the rejected step never mounts.
  useLayoutEffect(() => {
    if (!pendingIntentRedirectRef.current) {
      return;
    }
    const stepId = getStepIdFromPath(location.pathname);
    if (!stepId || stepId === STEP_IDS.INTENT) {
      pendingIntentRedirectRef.current = false;
      setResumeDecisionReady(true);
    }
  }, [location.pathname]);

  const pathWithFlowSearch = useCallback((path) => {
    const current = snapshotRef.current;
    const search = buildFlowSearch(
      current.params,
      landingSearchRef.current
    );
    return `${path}${search}`;
  }, []);

  const setAnswer = useCallback(
    (key, value) => {
      persist((prev) => ({
        ...prev,
        answers: {
          ...prev.answers,
          [key]: value,
        },
        // Edited answers are no longer URL prefills — do not skip on goNext.
        prefilledKeys: (prev.prefilledKeys || []).filter(
          (prefilledKey) => prefilledKey !== key
        ),
      }));
    },
    [persist]
  );

  const setAnswers = useCallback(
    (patch) => {
      persist((prev) => ({
        ...prev,
        answers: {
          ...prev.answers,
          ...patch,
        },
      }));
    },
    [persist]
  );

  const navigateToStep = useCallback(
    (stepId, { replace = false } = {}) => {
      const path = getPathForStepId(stepId);
      if (!path) {
        return;
      }

      persist((prev) => {
        let visitedStepIds = prev.visitedStepIds.slice();
        const last = visitedStepIds[visitedStepIds.length - 1];
        if (last !== stepId) {
          visitedStepIds = [...visitedStepIds, stepId];
        }

        return {
          ...prev,
          currentStepId: stepId,
          visitedStepIds,
        };
      });

      const target = pathWithFlowSearch(path);
      if (replace) {
        history.replace(target);
      } else {
        history.push(target);
      }
    },
    [history, pathWithFlowSearch, persist]
  );

  // Sync currentStepId from the URL. On browser Back/Forward (POP), also trim
  // visitedStepIds so the in-app ← arrow matches the browser history.
  // Skip the first run so it cannot race with client hydrate and wipe URL fills.
  const skipInitialPathSyncRef = useRef(true);
  useEffect(() => {
    if (skipInitialPathSyncRef.current) {
      skipInitialPathSyncRef.current = false;
      return;
    }

    if (skipPathSyncRef.current) {
      return;
    }

    const stepId = getStepIdFromPath(location.pathname);
    if (!stepId) {
      return;
    }

    const isPop = history.action === 'POP';

    if (isPop) {
      // Write the trimmed stack to the ref before React state flushes so a
      // same-turn goBack() does not rebuild from pre-POP visitedStepIds.
      const prev = snapshotRef.current;
      const nextVisited = trimVisitedToStep(prev.visitedStepIds, stepId);
      if (
        prev.currentStepId === stepId &&
        nextVisited.length === prev.visitedStepIds.length &&
        nextVisited.every((id, i) => id === prev.visitedStepIds[i])
      ) {
        return;
      }
      const next = normalizeSnapshot({
        ...prev,
        currentStepId: stepId,
        visitedStepIds: nextVisited,
      });
      snapshotRef.current = next;
      persist(next);
      return;
    }

    persist((prev) => {
      if (prev.currentStepId === stepId) {
        return prev;
      }

      return {
        ...prev,
        currentStepId: stepId,
      };
    });
  }, [history.action, location.pathname, persist]);

  const lastViewedStepRef = useRef(null);
  useEffect(() => {
    // Wait for hydrate so a saved draft does not briefly count the hidden
    // intent URL before offerResume flips true (which would also suppress the
    // later visible step via lastViewedStepRef).
    if (!resumeDecisionReady || offerResume) {
      return;
    }
    const stepId = getStepIdFromPath(location.pathname);
    if (!stepId || lastViewedStepRef.current === stepId) {
      return;
    }
    lastViewedStepRef.current = stepId;
    trackPostFlowStepViewed(stepId);
  }, [location.pathname, offerResume, resumeDecisionReady]);

  const registerPrepareExit = useCallback((prepareExit) => {
    prepareExitRef.current = prepareExit;
    return () => {
      if (prepareExitRef.current === prepareExit) {
        prepareExitRef.current = null;
      }
    };
  }, []);

  const beginExitTransition = useCallback((direction, action) => {
    if (transitionBusyRef.current) {
      return;
    }
    transitionBusyRef.current = true;

    // Capture the outgoing step before the route changes, then navigate
    // immediately so the next step is already underneath the exit overlay.
    if (prepareExitRef.current) {
      prepareExitRef.current(direction);
      action();
      return;
    }

    // Without a shell prepare handler (e.g. unit tests), finish immediately.
    action();
    transitionBusyRef.current = false;
  }, []);

  const completeExitTransition = useCallback(() => {
    transitionBusyRef.current = false;
  }, []);

  const goNext = useCallback(() => {
    const current = snapshotRef.current;
    const currentStepId =
      getStepIdFromPath(location.pathname) || current.currentStepId;
    const nextStepId = getNextStepId(currentStepId, current.answers, {
      prefilledKeys: current.prefilledKeys,
      visitedStepIds: current.visitedStepIds,
    });
    if (!nextStepId) {
      return;
    }
    beginExitTransition('forward', () => navigateToStep(nextStepId));
  }, [beginExitTransition, location.pathname, navigateToStep]);

  const goSkip = useCallback(() => {
    const current = snapshotRef.current;
    const currentStepId =
      getStepIdFromPath(location.pathname) || current.currentStepId;
    const nextStepId = getSkipStepId(currentStepId, current.answers, {
      prefilledKeys: current.prefilledKeys,
      visitedStepIds: current.visitedStepIds,
    });
    if (!nextStepId) {
      return;
    }
    beginExitTransition('forward', () => navigateToStep(nextStepId));
  }, [beginExitTransition, location.pathname, navigateToStep]);

  const goBack = useCallback(() => {
    const current = snapshotRef.current;
    // Prefer history.location: after browser Back it is current before useLocation re-renders.
    const currentStepId =
      getStepIdFromPath(history.location.pathname) ||
      getStepIdFromPath(location.pathname) ||
      current.currentStepId;
    const { previousStepId, nextVisited } = resolveBackFromVisited(
      current.visitedStepIds,
      currentStepId
    );

    beginExitTransition('back', () => {
      persist({
        ...current,
        currentStepId: previousStepId,
        visitedStepIds: nextVisited,
      });
      history.replace(pathWithFlowSearch(getPathForStepId(previousStepId)));
    });
  }, [
    beginExitTransition,
    history,
    location.pathname,
    pathWithFlowSearch,
    persist,
  ]);

  const restart = useCallback(() => {
    const stepName =
      getStepIdFromPath(location.pathname) ||
      snapshotRef.current.currentStepId ||
      STEP_IDS.INTENT;
    // Track only if beginExitTransition accepts the action (busy transitions
    // are ignored and must not inflate restart counts).
    beginExitTransition('back', () => {
      trackPostFlowRestarted(stepName);
      storageRef.current.clear();
      const nextSnapshot = applyQueryToSnapshot(
        createEmptySnapshot(),
        landingSearchRef.current
      );
      snapshotRef.current = nextSnapshot;
      setSnapshot(nextSnapshot);
      history.push(
        `${getPathForStepId(STEP_IDS.INTENT)}${buildFlowSearch(
          nextSnapshot.params,
          landingSearchRef.current
        )}`
      );
    });
  }, [applyQueryToSnapshot, beginExitTransition, history, location.pathname]);

  const resumeProgress = useCallback(() => {
    skipPathSyncRef.current = false;
    setOfferResume(false);
    const stepId = snapshotRef.current.currentStepId || STEP_IDS.INTENT;
    trackPostFlowResumed(stepId);
    if (stepId === STEP_IDS.INTENT) {
      return;
    }
    navigateToStep(stepId, { replace: true });
  }, [navigateToStep]);

  const startAgain = useCallback(() => {
    const stepName = snapshotRef.current.currentStepId || STEP_IDS.INTENT;
    trackPostFlowRestarted(stepName);
    skipPathSyncRef.current = false;
    setOfferResume(false);
    storageRef.current.clear();
    const nextSnapshot = applyQueryToSnapshot(
      createEmptySnapshot(),
      landingSearchRef.current
    );
    snapshotRef.current = nextSnapshot;
    setSnapshot(nextSnapshot);
    history.replace(
      `${getPathForStepId(STEP_IDS.INTENT)}${buildFlowSearch(
        nextSnapshot.params,
        landingSearchRef.current
      )}`
    );
  }, [applyQueryToSnapshot, history]);

  const pathStepId = getStepIdFromPath(location.pathname);
  const currentStepId = offerResume
    ? snapshot.currentStepId
    : pathStepId || snapshot.currentStepId;
  const progress = getProgressFraction(currentStepId);
  const canGoBack = !offerResume && currentStepId !== STEP_IDS.INTENT;

  const value = useMemo(
    () => ({
      answers: snapshot.answers,
      params: snapshot.params,
      currentStepId,
      visitedStepIds: snapshot.visitedStepIds,
      progress,
      canGoBack,
      offerResume,
      // False until hydrate decides resume vs mid-flow redirect (gates step UI).
      flowReady: resumeDecisionReady,
      setAnswer,
      setAnswers,
      goNext,
      goSkip,
      goBack,
      restart,
      resumeProgress,
      startAgain,
      registerPrepareExit,
      completeExitTransition,
    }),
    [
      snapshot.answers,
      snapshot.params,
      snapshot.visitedStepIds,
      currentStepId,
      progress,
      canGoBack,
      offerResume,
      resumeDecisionReady,
      setAnswer,
      setAnswers,
      goNext,
      goSkip,
      goBack,
      restart,
      resumeProgress,
      startAgain,
      registerPrepareExit,
      completeExitTransition,
    ]
  );

  return (
    <PostAJobFlowContext.Provider value={value}>
      {children}
    </PostAJobFlowContext.Provider>
  );
};

PostAJobFlowProvider.propTypes = {
  children: PropTypes.node.isRequired,
  storage: PropTypes.shape({
    load: PropTypes.func.isRequired,
    save: PropTypes.func.isRequired,
    clear: PropTypes.func.isRequired,
  }),
};

export const usePostAJobFlow = () => {
  const context = useContext(PostAJobFlowContext);
  if (!context) {
    throw new Error('usePostAJobFlow must be used within PostAJobFlowProvider');
  }
  return context;
};
